import tinycolor from 'tinycolor2';
import { black, gray3 } from 'styles/global_defaults/colors';

// Default colors
const DEFAULT_FONT_LIGHT_COLOR = '#fff';
const DEFAULT_FONT_DARK_COLOR = black;
const DEFAULT_BACKGROUND_LIGHT_COLOR = '#fff';
const DEFAULT_BACKGROUND_DARK_COLOR = gray3;

/**
 * Returns the appropriate font color for a given background color.
 * Returns '#fff' for dark backgrounds, and `black` (defined as '#1d2126') for light backgrounds.
 *
 * @param backgroundColor - The background color to analyze
 * @returns '#fff' or black (i.e., '#1d2126')
 */
const getFontColorFromBackground = (backgroundColor: string | null | undefined): string => getContrastingColor(
  backgroundColor,
  DEFAULT_FONT_LIGHT_COLOR,
  DEFAULT_FONT_DARK_COLOR,
);

/**
 * Returns the appropriate background color for a given font color.
 * Returns DEFAULT_BACKGROUND_DARK_COLOR (gray3) for light fonts, DEFAULT_BACKGROUND_LIGHT_COLOR ('#fff') for dark fonts.
 *
 * @param fontColor - The font color to analyze
 * @returns DEFAULT_BACKGROUND_DARK_COLOR (gray3) or DEFAULT_BACKGROUND_LIGHT_COLOR ('#fff')
 */
const getBackgroundColorFromFontColor = (fontColor: string | null | undefined): string => getContrastingColor(
  fontColor,
  DEFAULT_BACKGROUND_LIGHT_COLOR,
  DEFAULT_BACKGROUND_DARK_COLOR,
);

const getContrastingColor = (
  color: string | null | undefined,
  defaultLightColor: string,
  defaultDarkColor: string,
): string => {
  if (!color) return defaultDarkColor;

  const colorObj = tinycolor(color);
  if (!colorObj.isValid()) return defaultDarkColor;

  return colorObj.isLight() ? defaultDarkColor : defaultLightColor;
};

/**
 * Core logic to determine if a color is light.
 *
 * @param color - The color to analyze
 * @param defaultValue - The default value to return if color is null, undefined, or invalid
 * @returns true if the color is light, false if dark, or defaultValue if color cannot be determined
 */
export const isColorLight = (color: string | null | undefined, defaultValue: boolean): boolean => {
  if (!color) return defaultValue;

  const colorObj = tinycolor(color);
  if (!colorObj.isValid()) return defaultValue;

  return colorObj.isLight();
};

/**
 * Determines if a background color is light.
 *
 * @param backgroundColor - The background color to analyze
 * @returns true if the background is light, false if dark
 */
const isBackgroundLight = (backgroundColor: string | null | undefined): boolean => isColorLight(
  backgroundColor,
  true,
);

/**
 * Determines if a font color is light.
 *
 * @param fontColor - The font color to analyze
 * @returns true if the font is light, false if dark
 */
const isFontColorLight = (fontColor: string | null | undefined): boolean => isColorLight(
  fontColor,
  false,
);

/**
 * Gets the font color, background color, and background image for a course/journey with proper fallback logic.
 * Works for both Course and Journey objects (since Journey is a superset of Course).
 *
 * Logic:
 * 1. If nameFontColor is explicitly set, return it along with headerColor as backgroundColor (or determine from font color if headerColor not present)
 * 2. If nameFontColor is not set and headerColor exists, calculate dynamically from headerColor
 * 3. If no headerColor, calculate dynamically from institution.brandColor
 * 4. Fallback to institutionBrandBarFontColor (on Course) - backgroundColor determined from font color
 * 5. Fallback to brandBarFontColor (on Institution) - backgroundColor determined from font color
 * 6. Default to DEFAULT_FONT_COLOR with DEFAULT_BACKGROUND_COLOR background (backgroundColor determined from font color)
 *
 * @param courseOrJourney - Course or Journey object
 * @param institution - Institution object (optional, for fallback)
 * @returns Object with fontColor (uppercase hex), backgroundColor (uppercase hex, determined from font color if not explicitly set), backgroundImage (string or null from headerBackground), isBackgroundLight (boolean indicating if background is light), and isFontColorLight (boolean indicating if font color is light)
 */
const getCourseHeaderMeta = (
  courseOrJourney?: {
    nameFontColor?: string | null;
    institutionBrandBarFontColor?: string | null;
    headerColor?: string | null;
    headerBackground?: string | null;
  },
  institution?: {
    brandBarFontColor?: string | null;
    brandColor?: string | null;
  },
): { fontColor: string; backgroundColor: string; backgroundImage: string | null; isBackgroundLight: boolean; isFontColorLight: boolean } => {
  const backgroundImage = courseOrJourney?.headerBackground || null;

  // 1. If nameFontColor is explicitly set, return it along with headerColor as backgroundColor
  // If headerColor is not present, determine backgroundColor from font color
  if (courseOrJourney?.nameFontColor) {
    const fontColor = courseOrJourney.nameFontColor.toUpperCase();
    const backgroundColor = courseOrJourney?.headerColor?.toUpperCase()
      || getBackgroundColorFromFontColor(fontColor);
    return {
      fontColor,
      backgroundColor,
      backgroundImage,
      isBackgroundLight: isBackgroundLight(backgroundColor),
      isFontColorLight: isFontColorLight(fontColor),
    };
  }

  // 2. Dynamic calculation from headerColor
  if (courseOrJourney?.headerColor) {
    const backgroundColor = courseOrJourney.headerColor.toUpperCase();
    const fontColor = getFontColorFromBackground(courseOrJourney.headerColor).toUpperCase();
    return {
      fontColor,
      backgroundColor,
      backgroundImage,
      isBackgroundLight: isBackgroundLight(backgroundColor),
      isFontColorLight: isFontColorLight(fontColor),
    };
  }

  // 3. Dynamic calculation from institution brandColor
  if (institution?.brandColor) {
    const backgroundColor = institution.brandColor.toUpperCase();
    const fontColor = getFontColorFromBackground(institution.brandColor).toUpperCase();
    return {
      fontColor,
      backgroundColor,
      backgroundImage,
      isBackgroundLight: isBackgroundLight(backgroundColor),
      isFontColorLight: isFontColorLight(fontColor),
    };
  }

  // 4. Fallback to institutionBrandBarFontColor (on Course)
  if (courseOrJourney?.institutionBrandBarFontColor) {
    const fontColor = courseOrJourney.institutionBrandBarFontColor.toUpperCase();
    const backgroundColor = getBackgroundColorFromFontColor(fontColor);
    return {
      fontColor,
      backgroundColor,
      backgroundImage,
      isBackgroundLight: isBackgroundLight(backgroundColor),
      isFontColorLight: isFontColorLight(fontColor),
    };
  }

  // 5. Fallback to brandBarFontColor (on Institution)
  if (institution?.brandBarFontColor) {
    const fontColor = institution.brandBarFontColor.toUpperCase();
    const backgroundColor = getBackgroundColorFromFontColor(fontColor);
    return {
      fontColor,
      backgroundColor,
      backgroundImage,
      isBackgroundLight: isBackgroundLight(backgroundColor),
      isFontColorLight: isFontColorLight(fontColor),
    };
  }

  // 6. Default
  const fontColor = DEFAULT_FONT_DARK_COLOR;
  const backgroundColor = getBackgroundColorFromFontColor(fontColor);
  return {
    fontColor,
    backgroundColor,
    backgroundImage,
    isBackgroundLight: isBackgroundLight(backgroundColor),
    isFontColorLight: isFontColorLight(fontColor),
  };
};

export default getCourseHeaderMeta;
